import { useNavigate } from 'react-router-dom';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { TaskIcon } from 'app/agriculture/icons';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useContext } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { TextArea } from 'app/agriculture/forms/components/TextArea/TextArea';
import { EntityParentSelectField } from 'app/agriculture/forms/components/EntityParentSelectField/EntityParentSelectField';
import { MapLocation } from 'app/agriculture/queries/types/MapLocation';
import { SpecificLocationField } from 'app/agriculture/forms/components/SpecificLocationField/SpecificLocationField';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { useRouteActions } from 'app/agriculture/router/useRouteActions';
import { useInitialParent } from 'app/agriculture/router/useInitialParent';
import { TaskPriority } from 'app/agriculture/queries/tasks/types/TaskPriority';
import { TaskCategoryDropdown } from 'app/agriculture/routes/Tasks/TaskCreate/components/TaskCategoryDropdown';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import { UserSelectField } from 'app/agriculture/forms/components/UserSelectField/UserSelectField';
import { TaskPriorityDropdown } from 'app/agriculture/routes/Tasks/TaskCreate/components/TaskPriorityDropdown';
import { useEntityCreateMutation } from 'app/agriculture/mutations/entity/create/useEntityCreateMutation';
import { useTaskCategories } from 'app/agriculture/queries/tasks/useTaskCategories';
import { startOfDay, endOfDay, isAfter, isDate } from 'date-fns';
import './TaskCreateForm.scss';
import { useAttachOptions } from 'app/agriculture/hooks/useAttachOptions/useAttachOptions';
import { taskAttachOptionTypes } from 'app/agriculture/routes/Tasks/utils/taskAttachOptionTypes';
import { tz, TZDate } from '@date-fns/tz';
import { DatePickerInput } from 'app/agriculture/forms/components/DatePicker/DatePickerInput';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';

interface TaskCreateFields {
    title: string;
    category: string;
    description: string | null;
    location: MapLocation | null;
    priority: TaskPriority;

    startDate?: Date;
    endDate?: Date;

    parentIds: (string | number)[];
    assignees: number[];
}

export function TaskCreateForm() {
    const navigate = useNavigate();
    const { timeZone } = useTimeZone();
    const routeActions = useRouteActions();
    const taskCategories = useTaskCategories();
    const { id: initialParentId } = useInitialParent() ?? {};
    const form = useForm<TaskCreateFields>({
        mode: 'all',
        defaultValues: {
            category: Array.from(taskCategories.values())[0]?.name,
            priority: 'Normal',

            startDate: startOfDay(new TZDate(new Date(), timeZone)),
            endDate: endOfDay(new TZDate(new Date(), timeZone)),

            parentIds: initialParentId == null ? [] : [initialParentId],
            assignees: [],
        },
        shouldFocusError: true,
    });

    const { mutate, isPending } = useEntityCreateMutation('Task');
    const { agricultureToastService } = useContext(ServicesContext);

    function onSuccess({ id }: { id: string }) {
        agricultureToastService
            .showToast({
                title: 'Task has been saved',
                subtext: 'The task was saved successfully!',
                type: AgricultureToastType.SUCCESS,
                variant: 'detailed',
                actions: [
                    {
                        text: 'Add Another Task',
                        onClick: () => {
                            routeActions.create('Task');
                        },
                        startIcon: 'add',
                    },
                    {
                        text: 'Edit Task',
                        onClick: () => {
                            routeActions.edit(id, { type: 'Task' });
                        },
                        startIcon: 'edit',
                        variant: 'textonly',
                    },
                ],
            })
            .catch((e: unknown) => {
                console.error(e);
            });
        form.reset();
        routeActions.goto(id, { type: 'Task' });
    }

    const onSubmit: SubmitHandler<TaskCreateFields> = (formData) => {
        mutate(formData, { onSuccess });
    };

    const attachOptions = useAttachOptions(taskAttachOptionTypes);

    return (
        <ContentBox title="Create Task" Icon={TaskIcon} className="TaskCreateForm" variant="md">
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <FormDiscardChangesBlocker />
                    <ContentBox.Section scrollable className="TaskCreateForm__fields">
                        <TaskCategoryDropdown />
                        <Input
                            label="Title"
                            className="TaskCreate__title-input"
                            {...form.register('title', { required: 'Please add a title' })}
                            errorMessage={form.formState.errors.title?.message}
                        />
                        <TextArea
                            label="Description"
                            {...form.register('description')}
                            className="TaskCreate__description-input"
                            errorMessage={form.formState.errors.description?.message}
                        />
                        <EntityParentSelectField
                            label="Attach Task To"
                            fieldName="parentIds"
                            attachOptions={attachOptions}
                        />
                        <SpecificLocationField
                            isPin
                            entityType="Task"
                            initialValue={null}
                            onChange={(value) => {
                                form.setValue('location', value, { shouldDirty: true });
                            }}
                        />
                        <UserSelectField fieldName="assignees" label="Assignees" />
                        <SimpleGrid columns={2}>
                            <TaskPriorityDropdown />
                        </SimpleGrid>
                        <SimpleGrid columns={2}>
                            <Controller
                                control={form.control}
                                name="startDate"
                                rules={{
                                    validate: {
                                        invalidDateRange: (value, fields) => {
                                            if (value == null || fields.endDate == null) return;
                                            if (isAfter(value, fields.endDate)) {
                                                return 'Start date must be before end date.';
                                            }
                                        },
                                        invalidDate: (value) => {
                                            if (value == null || !isDate(value)) {
                                                return 'Please enter a valid date';
                                            }
                                        },
                                    },
                                }}
                                render={({ field, fieldState, formState }) => (
                                    <DatePickerInput
                                        {...field}
                                        id="entity-start-date-input"
                                        label="Start date"
                                        initialValue={formState.defaultValues?.startDate}
                                        errorMessage={fieldState.error?.message}
                                        onChange={(date?: Date) => {
                                            if (date == null) {
                                                field.onChange();
                                                return;
                                            }

                                            field.onChange(startOfDay(date, { in: tz(timeZone) }));
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                control={form.control}
                                name="endDate"
                                rules={{
                                    validate: {
                                        invalidDate: (value) => {
                                            if (value == null || !isDate(value)) {
                                                return 'Please enter a valid date';
                                            }
                                        },
                                    },
                                }}
                                render={({ field, fieldState, formState }) => (
                                    <DatePickerInput
                                        {...field}
                                        id="entity-end-date-input"
                                        label="End date"
                                        initialValue={formState.defaultValues?.endDate}
                                        errorMessage={fieldState.error?.message}
                                        onChange={(date?: Date) => {
                                            if (date == null) {
                                                field.onChange();
                                                return;
                                            }

                                            field.onChange(endOfDay(date, { in: tz(timeZone) }));
                                            void form.trigger('startDate');
                                        }}
                                    />
                                )}
                            />
                        </SimpleGrid>
                        <Actions
                            sticky
                            primaryAction={{
                                text: 'Create task',
                                type: 'submit',
                                isLoading: isPending,
                            }}
                            backAction={{
                                text: 'Back',
                                onClick: () => {
                                    navigate(-1);
                                },
                            }}
                        />
                    </ContentBox.Section>
                </form>
            </FormProvider>
        </ContentBox>
    );
}
